import { parseEmailHeaders, analyzeHeadersWithIntel, getHeaderList, parseSummaryFields, parseReceivedChain, extractIPsFromReceived, parseAuthResults, parseAntiSpamHeaders } from '../services/email-header-parser.js';
import { batchLookupIPs } from '../services/geoip.js';
import { enrichIP } from '../services/threat-intel.js';
import { addIpEnrichment } from '../services/storage.js';
import { html, concurrencyPool } from '../services/util.js';
import './toast-container.js';

const template = document.createElement('template');
template.innerHTML = `
  <div class="section-header">
    <h1>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:8px">
        <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/>
      </svg>
      Email Header Analysis
    </h1>
  </div>

  <div class="paste-area">
    <div class="card" style="margin-bottom:16px">
      <div class="card-header">
        <div class="card-title">Paste Email Headers</div>
        <div class="section-actions">
          <label for="eml-file-input" class="btn btn-sm btn-secondary" style="cursor:pointer">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Open File
          </label>
          <input type="file" id="eml-file-input" accept=".eml,.txt" style="display:none">
        </div>
      </div>
      <textarea id="header-input" rows="12" placeholder="Paste raw RFC 5322 email headers here...&#10;&#10;Example:&#10;From: sender@example.com&#10;To: recipient@example.com&#10;Subject: Test&#10;Date: Mon, 1 Jan 2024 00:00:00 +0000&#10;Received: from mail.example.com (1.2.3.4) by mx.example.org with ESMTP...&#10;Authentication-Results: mx.example.org; spf=pass smtp.mailfrom=example.com" style="width:100%;font-family:var(--font-mono);font-size:0.8rem;resize:vertical;tab-size:2"></textarea>
      <div id="paste-actions" style="display:flex;gap:8px;margin-top:12px">
        <button class="btn btn-primary" id="analyze-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          Analyze Headers
        </button>
        <button class="btn btn-secondary" id="clear-btn">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Clear
        </button>
      </div>
    </div>

    <div class="drop-zone" id="header-drop-zone" style="margin-bottom:16px">
      <div class="drop-icon">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      </div>
      <div class="drop-text">Or drop an .eml or .txt file here</div>
      <div class="drop-hint">Drag and drop a raw email file to auto-fill the header textarea</div>
    </div>
  </div>

  <div id="progress-area" style="display:none; margin-bottom:16px;">
    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
        <div id="progress-step" style="font-size:0.85rem;color:var(--text-muted)">Step: —</div>
      </div>
      <div id="progress-text" style="margin-bottom: 8px;">Processing...</div>
      <div class="progress-track">
        <div id="progress-bar" class="progress-fill" style="width: 0%;"></div>
      </div>
    </div>
  </div>

  <div id="results-area" class="results-area" style="display:none">
    <!-- Summary Section -->
    <div class="section-header" style="margin-top:24px">
      <h2>Summary</h2>
    </div>
    <div class="card" id="summary-section" style="margin-bottom:16px">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 24px">
        <div class="detail-row"><span class="detail-label">From</span><span class="detail-value" id="r-summary-from">—</span></div>
        <div class="detail-row"><span class="detail-label">To</span><span class="detail-value" id="r-summary-to">—</span></div>
        <div class="detail-row"><span class="detail-label">Subject</span><span class="detail-value" id="r-summary-subject">—</span></div>
        <div class="detail-row"><span class="detail-label">Date</span><span class="detail-value" id="r-summary-date">—</span></div>
        <div class="detail-row"><span class="detail-label">Message-ID</span><span class="detail-value" id="r-summary-messageid">—</span></div>
        <div class="detail-row"><span class="detail-label">Return-Path</span><span class="detail-value" id="r-summary-returnpath">—</span></div>
      </div>
    </div>

    <!-- Auth Results Section -->
    <div class="section-header" style="margin-top:24px">
      <h2>Authentication Results</h2>
    </div>
    <div id="auth-section" style="margin-bottom:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px"></div>

    <!-- Received Chain Section -->
    <div class="section-header" style="margin-top:24px">
      <h2>Received Chain</h2>
    </div>
    <div id="received-section" style="margin-bottom:16px"></div>

    <!-- Anti-Spam Section -->
    <div class="section-header" style="margin-top:24px">
      <h2>Anti-Spam Headers</h2>
    </div>
    <div id="antispam-section" style="margin-bottom:16px"></div>

    <!-- Threat Section -->
    <div class="section-header" style="margin-top:24px">
      <h2>Threat Indicators</h2>
    </div>
    <div id="threat-section" style="margin-bottom:16px"></div>

    <!-- IP Section -->
    <div class="section-header" style="margin-top:24px">
      <h2>IP Addresses Found</h2>
    </div>
    <div id="ip-section" style="margin-bottom:16px"></div>
  </div>
`;

export class EmailHeaderPane extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = template.innerHTML;
    this._result = null;
    this._enrichments = {};
    this._analyzing = false;
  }

  connectedCallback() {
    this.querySelector('#analyze-btn').addEventListener('click', () => this.analyzeHeaders());
    this.querySelector('#clear-btn').addEventListener('click', () => this.clearAll());
    this.querySelector('#eml-file-input').addEventListener('change', (e) => this.handleFilePick(e));

    const dropZone = this.querySelector('#header-drop-zone');
    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('drag-over');
    });
    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('drag-over');
    });
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('drag-over');
      this.handleFileDrop(e.dataTransfer.files);
    });

    // Support paste event on textarea for convenience
    this.querySelector('#header-input').addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.analyzeHeaders();
      }
    });
  }

  _setProgress(step, text, pct) {
    const area = this.querySelector('#progress-area');
    const stepEl = this.querySelector('#progress-step');
    const textEl = this.querySelector('#progress-text');
    const barEl = this.querySelector('#progress-bar');
    area.style.display = '';
    if (stepEl) stepEl.textContent = `Step: ${step}`;
    if (textEl) textEl.textContent = text;
    if (barEl && pct != null) barEl.style.width = `${Math.min(pct, 100)}%`;
  }

  _hideProgress() {
    this.querySelector('#progress-area').style.display = 'none';
  }

  handleFilePick(e) {
    const file = e.target.files[0];
    if (!file) return;
    this.readFile(file);
    e.target.value = '';
  }

  handleFileDrop(files) {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.name.match(/\.(eml|txt)$/i)) {
      window.showToast('Please drop an .eml or .txt file', 'warning');
      return;
    }
    this.readFile(file);
  }

  readFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.querySelector('#header-input').value = e.target.result;
      window.showToast(`Loaded ${file.name} (${(file.size / 1024).toFixed(1)} KB)`, 'info');
    };
    reader.onerror = () => {
      window.showToast(`Failed to read file: ${file.name}`, 'error');
    };
    reader.readAsText(file);
  }

  clearAll() {
    this.querySelector('#header-input').value = '';
    this.querySelector('#results-area').style.display = 'none';
    this._hideProgress();
    this._result = null;
    this._enrichments = {};
    this._analyzing = false;
  }

  async analyzeHeaders() {
    if (this._analyzing) return;

    const rawText = this.querySelector('#header-input').value.trim();
    if (!rawText) {
      window.showToast('Please paste email headers first', 'warning');
      return;
    }

    this._analyzing = true;
    this.querySelector('#analyze-btn').disabled = true;

    try {
      // Step 1: Parse headers
      this._setProgress('Parsing', 'Parsing email headers...', 5);
      const parsed = parseEmailHeaders(rawText);

      if (parsed.error) {
        window.showToast(`Error parsing headers: ${parsed.error}`, 'error');
        this._analyzing = false;
        this.querySelector('#analyze-btn').disabled = false;
        this._hideProgress();
        return;
      }

      if (!parsed.summary && parsed.rawHeaders.length === 0) {
        window.showToast('No valid email headers found. Check the format and try again.', 'warning');
        this._analyzing = false;
        this.querySelector('#analyze-btn').disabled = false;
        this._hideProgress();
        return;
      }

      this._result = parsed;

      // Step 2: Extract unique IPs from Received chain
      const uniqueIPs = parsed.ips;
      this._setProgress('GeoIP', `Resolving IP locations (${uniqueIPs.length} unique IPs)...`, 10);

      const enrichments = {};
      let processedIPs = [];

      if (uniqueIPs.length > 0) {
        // Step 3: GeoIP lookups
        const geoResults = await batchLookupIPs(uniqueIPs, (done, total) => {
          const pct = 10 + (done / total) * 35;
          this._setProgress('GeoIP', `GeoIP: ${done}/${total}`, pct);
        });

        for (const geo of geoResults) {
          if (geo && geo.ip) {
            enrichments[geo.ip] = geo;
          }
        }

        // Step 4: Threat intel enrichment
        const publicIPs = geoResults.filter(e => e && !e.isPrivate && e.ip);
        this._setProgress('Threat Intel', `Checking threat intel feeds...`, 45);

        let intelDone = 0;
        const intelTotal = publicIPs.length;
        await concurrencyPool(publicIPs, async (e) => {
          const threatIntel = await enrichIP(e.ip);
          const updated = { ...e, threatIntel };
          await addIpEnrichment(updated);
          enrichments[e.ip] = updated;
          intelDone++;
          const pct = 45 + (intelDone / Math.max(intelTotal, 1)) * 15;
          this._setProgress('Threat Intel', `Intel: ${intelDone}/${intelTotal}`, pct);
        }, 10);

        processedIPs = uniqueIPs;
      }

      this._enrichments = enrichments;

      // Step 5: Analyze with intel
      this._setProgress('Analyzing', 'Running threat detection on headers...', 90);
      const result = analyzeHeadersWithIntel(parsed, enrichments);

      this._result = result;

      // Step 6: Render results
      this._setProgress('Rendering', 'Rendering results...', 95);
      this.renderResults(result, enrichments);

      this._setProgress('Complete', 'Analysis complete', 100);
      window.showToast(`Header analysis complete — ${(result.indicators || []).length} threat indicator(s) found`, 'success');

      setTimeout(() => this._hideProgress(), 1500);
    } catch (err) {
      console.error('[email-header-pane] analysis failed:', err);
      window.showToast(`Analysis failed: ${err.message}`, 'error');
      this._hideProgress();
    } finally {
      this._analyzing = false;
      this.querySelector('#analyze-btn').disabled = false;
    }
  }

  renderResults(result, enrichments) {
    const resultsArea = this.querySelector('#results-area');
    resultsArea.style.display = '';

    this.renderSummary(result.summary);
    this.renderAuthResults(result.authResults);
    this.renderReceivedChain(result.receivedChain);
    this.renderAntiSpam(result.antiSpam);
    this.renderThreats(result.indicators);
    this.renderIPs(result.ips, enrichments);
  }

  renderSummary(summary) {
    if (!summary) {
      this.querySelector('#r-summary-from').textContent = '—';
      this.querySelector('#r-summary-to').textContent = '—';
      this.querySelector('#r-summary-subject').textContent = '—';
      this.querySelector('#r-summary-date').textContent = '—';
      this.querySelector('#r-summary-messageid').textContent = '—';
      this.querySelector('#r-summary-returnpath').textContent = '—';
      return;
    }

    this.querySelector('#r-summary-from').textContent = summary.from || '—';
    this.querySelector('#r-summary-to').textContent = summary.to || '—';
    this.querySelector('#r-summary-subject').textContent = summary.subject || '—';
    this.querySelector('#r-summary-date').textContent = summary.date || '—';
    this.querySelector('#r-summary-messageid').textContent = summary.messageId || '—';
    this.querySelector('#r-summary-returnpath').textContent = summary.returnPath || '—';
  }

  renderAuthResults(authResults) {
    const container = this.querySelector('#auth-section');
    if (!authResults) {
      container.innerHTML = '<div class="card" style="grid-column:1/-1"><div class="card-title">No authentication results found</div></div>';
      return;
    }

    const methods = [
      { key: 'spf', label: 'SPF' },
      { key: 'dkim', label: 'DKIM' },
      { key: 'dmarc', label: 'DMARC' },
      { key: 'arc', label: 'ARC' },
    ];

    container.innerHTML = methods.map(m => {
      const data = authResults[m.key];
      if (!data) {
        return `<div class="card" style="text-align:center">
          <div style="font-size:0.75rem;color:var(--text-muted);margin-bottom:6px">${m.label}</div>
          <span class="badge" style="background:var(--bg-tertiary);color:var(--text-muted)">Not Found</span>
        </div>`;
      }

      const badge = this._authBadge(data.result);
      let details = '';
      if (data.domain) details += `<div style="font-size:0.72rem;color:var(--text-muted);margin-top:4px">${html(data.domain)}</div>`;
      if (data.selector) details += `<div style="font-size:0.72rem;color:var(--text-muted)">selector: ${html(data.selector)}</div>`;

      return `<div class="card" style="text-align:center">
        <div style="font-size:0.75rem;color:var(--text-muted);margin-bottom:6px">${m.label}</div>
        <span class="badge" style="background:${badge.bg};color:${badge.color}">${badge.label}</span>
        ${details}
      </div>`;
    }).join('');
  }

  _authBadge(result) {
    const r = (result || '').toLowerCase();
    if (r === 'pass') return { label: 'PASS', bg: 'var(--accent-green-dim)', color: 'var(--accent-green)' };
    if (r === 'fail') return { label: 'FAIL', bg: 'var(--accent-red-dim)', color: 'var(--accent-red)' };
    if (r === 'softfail') return { label: 'SOFTFAIL', bg: 'var(--accent-orange-dim)', color: 'var(--accent-orange)' };
    if (r === 'none') return { label: 'NONE', bg: 'var(--bg-tertiary)', color: 'var(--text-muted)' };
    if (r === 'neutral') return { label: 'NEUTRAL', bg: 'var(--accent-yellow-dim)', color: 'var(--accent-yellow)' };
    if (r === 'permerror') return { label: 'PERMERROR', bg: 'var(--accent-red-dim)', color: 'var(--accent-red)' };
    if (r === 'temperror') return { label: 'TEMPERROR', bg: 'var(--accent-orange-dim)', color: 'var(--accent-orange)' };
    return { label: r.toUpperCase(), bg: 'var(--bg-tertiary)', color: 'var(--text-muted)' };
  }

  renderReceivedChain(receivedChain) {
    const container = this.querySelector('#received-section');

    if (!receivedChain || receivedChain.length === 0) {
      container.innerHTML = '<div class="card"><div class="card-title" style="color:var(--text-muted)">No Received headers found</div></div>';
      return;
    }

    container.innerHTML = receivedChain.map((hop, idx) => {
      const isOrigin = idx === 0;
      const isLast = idx === receivedChain.length - 1;
      const arrow = isLast ? '' : `
        <div style="display:flex;flex-direction:column;align-items:center;padding:4px 0;color:var(--text-muted)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
        </div>`;

      return `
        <div class="card" style="margin-bottom:0;border-left:3px solid ${isOrigin ? 'var(--accent-green)' : isLast ? 'var(--accent-blue)' : 'var(--border-color)'}">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
            <span style="font-size:0.72rem;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em">
              ${isOrigin ? 'Origin' : isLast ? 'Destination' : `Hop ${hop.hop || idx + 1}`}
            </span>
            ${hop.timestamp ? `<span style="font-size:0.72rem;color:var(--text-muted)">${html(hop.timestamp)}</span>` : ''}
          </div>
          <div style="display:grid;grid-template-columns:auto 1fr;gap:3px 12px;font-size:0.82rem">
            ${hop.from ? `<span style="color:var(--text-muted)">from</span><span style="font-family:var(--font-mono);font-size:0.78rem;word-break:break-all">${this._highlightIPs(hop.from)}</span>` : ''}
            ${hop.by ? `<span style="color:var(--text-muted)">by</span><span style="font-family:var(--font-mono);font-size:0.78rem">${html(hop.by)}</span>` : ''}
            ${hop.with ? `<span style="color:var(--text-muted)">with</span><span>${html(hop.with)}</span>` : ''}
            ${hop.via ? `<span style="color:var(--text-muted)">via</span><span>${html(hop.via)}</span>` : ''}
            ${hop.id ? `<span style="color:var(--text-muted)">id</span><span style="font-size:0.72rem;word-break:break-all">${html(hop.id)}</span>` : ''}
            ${hop.for ? `<span style="color:var(--text-muted)">for</span><span>${html(hop.for)}</span>` : ''}
          </div>
        </div>
        ${arrow}
      `;
    }).join('');
  }

  _highlightIPs(text) {
    if (!text) return html(text);
    const ipv4 = /\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/g;
    const escaped = html(text);
    return escaped.replace(ipv4, (match) => {
      return `<span style="color:var(--accent-cyan);font-weight:600;cursor:pointer" class="clickable-ip" data-ip="${match}">${match}</span>`;
    });
  }

  renderAntiSpam(antiSpam) {
    const container = this.querySelector('#antispam-section');

    if (!antiSpam) {
      container.innerHTML = '<div class="card"><div class="card-title" style="color:var(--text-muted)">No anti-spam headers</div></div>';
      return;
    }

    const sections = [];

    if (antiSpam.forefront && Object.keys(antiSpam.forefront).length > 0) {
      sections.push({
        title: 'X-Forefront-Antispam-Report',
        items: Object.entries(antiSpam.forefront),
      });
    }

    if (antiSpam.exchange && Object.keys(antiSpam.exchange).length > 0) {
      sections.push({
        title: 'Exchange Headers',
        items: Object.entries(antiSpam.exchange),
      });
    }

    if (antiSpam.proofpoint && Object.keys(antiSpam.proofpoint).length > 0) {
      sections.push({
        title: 'Proofpoint Headers',
        items: Object.entries(antiSpam.proofpoint),
      });
    }

    if (antiSpam.spam && Object.keys(antiSpam.spam).length > 0) {
      sections.push({
        title: 'Spam Headers',
        items: Object.entries(antiSpam.spam),
      });
    }

    if (sections.length === 0) {
      container.innerHTML = '<div class="card"><div class="card-title" style="color:var(--text-muted)">No anti-spam headers found</div></div>';
      return;
    }

    container.innerHTML = sections.map(s => `
      <div class="card" style="margin-bottom:10px">
        <div class="card-title" style="font-size:0.8rem;margin-bottom:8px">${html(s.title)}</div>
        <div style="display:grid;grid-template-columns:auto 1fr;gap:3px 12px;font-size:0.78rem">
          ${s.items.map(([key, val]) => `
            <span style="color:var(--text-muted);white-space:nowrap;font-family:var(--font-mono)">${html(key)}</span>
            <span style="word-break:break-all">${html(typeof val === 'string' ? val : JSON.stringify(val))}</span>
          `).join('')}
        </div>
      </div>
    `).join('');
  }

  renderThreats(indicators) {
    const container = this.querySelector('#threat-section');

    if (!indicators || indicators.length === 0) {
      container.innerHTML = '<div class="card"><div class="card-title" style="color:var(--text-muted);display:flex;align-items:center;gap:8px">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>' +
        'No threat indicators detected</div></div>';
      return;
    }

    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    const sorted = [...indicators].sort((a, b) => {
      return (severityOrder[a.severity] ?? 99) - (severityOrder[b.severity] ?? 99);
    });

    container.innerHTML = sorted.map(ind => {
      const sevStyle = this._severityStyle(ind.severity);
      return `
        <div class="card" style="margin-bottom:8px;border-left:3px solid ${sevStyle.color}">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
            <span class="severity-dot ${ind.severity}"></span>
            <span class="badge badge-severity-${ind.severity}">${ind.severity}</span>
            <span style="font-weight:600;font-size:0.85rem">${html(ind.title)}</span>
            ${ind.source ? `<span style="margin-left:auto;font-size:0.68rem;color:var(--text-muted)">${html(ind.source)}</span>` : ''}
          </div>
          <div style="font-size:0.8rem;color:var(--text-secondary);padding-left:4px">${html(ind.description)}</div>
        </div>
      `;
    }).join('');
  }

  _severityStyle(severity) {
    switch (severity) {
      case 'critical': return { color: 'var(--accent-red)' };
      case 'high': return { color: 'var(--accent-orange)' };
      case 'medium': return { color: 'var(--accent-yellow)' };
      case 'low': return { color: 'var(--accent-green)' };
      default: return { color: 'var(--text-muted)' };
    }
  }

  renderIPs(ips, enrichments) {
    const container = this.querySelector('#ip-section');

    if (!ips || ips.length === 0) {
      container.innerHTML = '<div class="card"><div class="card-title" style="color:var(--text-muted)">No IP addresses found in headers</div></div>';
      return;
    }

    const rows = ips.map(ip => {
      const e = enrichments[ip];
      const country = e ? (e.country || e.countryCode || '—') : '—';
      const isPrivate = e && e.isPrivate;

      // Threat intel score summary
      let scoreLabel = '—';
      let scoreColor = 'var(--text-muted)';
      if (e && e.threatIntel) {
        const ti = e.threatIntel;
        const scores = [];
        if (ti.abuseIPDB && ti.abuseIPDB.abuseConfidenceScore != null) {
          scores.push(ti.abuseIPDB.abuseConfidenceScore);
        }
        if (ti.alienVault && ti.alienVault.pulseCount > 0) {
          scores.push(Math.min(ti.alienVault.pulseCount * 10, 100));
        }
        if (scores.length > 0) {
          const max = Math.max(...scores);
          scoreLabel = max;
          if (max >= 70) scoreColor = 'var(--accent-red)';
          else if (max >= 30) scoreColor = 'var(--accent-orange)';
          else scoreColor = 'var(--accent-green)';
        }
      }

      const enrichmentStatus = isPrivate
        ? '<span class="badge" style="background:var(--bg-tertiary);color:var(--text-muted)">Private</span>'
        : e && e.country
          ? '<span class="badge" style="background:var(--accent-green-dim);color:var(--accent-green)">Enriched</span>'
          : '<span class="badge" style="background:var(--accent-yellow-dim);color:var(--accent-yellow)">Pending</span>';

      return `
        <div class="import-item ip-row" data-ip="${html(ip)}">
          <div class="import-info">
            <div class="import-filename" style="font-family:var(--font-mono);font-size:0.82rem">
              <span class="clickable-ip" data-ip="${html(ip)}" style="cursor:pointer;color:var(--accent-cyan)">${html(ip)}</span>
            </div>
            <div class="import-meta" style="display:flex;gap:8px;align-items:center;margin-top:4px">
              <span>${html(country)}</span>
              <span style="color:var(--text-muted)">·</span>
              <span>Score: <span style="font-weight:700;color:${scoreColor}">${scoreLabel}</span></span>
            </div>
          </div>
          <div class="import-status">
            ${enrichmentStatus}
          </div>
          <div class="import-actions">
            <button class="btn btn-sm btn-secondary view-ip-btn" data-ip="${html(ip)}" title="View enrichment details">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="card" style="padding:0">
        ${rows}
      </div>
    `;

    // Wire up click events for IP viewing
    container.querySelectorAll('.clickable-ip, .view-ip-btn').forEach(el => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const ip = el.dataset.ip;
        const enrichment = enrichments[ip] || null;
        document.dispatchEvent(new CustomEvent('threatscope:show-detail', {
          detail: { type: 'ip', ip, enrichment },
        }));
      });
    });
  }
}

customElements.define('email-header-pane', EmailHeaderPane);
